import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { BehaviorSubject, combineLatest, EMPTY, from, Observable, of, OperatorFunction, race } from 'rxjs';
import { catchError, filter, map, mapTo, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { UserService } from '@root/login/services/user.service';
import * as UserActions from './user.actions';
import { GetLoginData, Logout } from './user.actions';
import { selectDashboardWidgets, selectGeoData, selectIsDashboardEnabled, selectIsStandAlonePropIntelEnabled, selectPropIntelChartSelections, selectPropIntelPreference, selectSessionTimeoutDefault, selectSessionTimeoutMsgShowDelay, selectUserInfo } from './user.selector';
import {
  ContactInfo,
  DisplayOptions,
  GeoData,
  LoginResponse,
  LoginStatus,
  PreferenceCode,
  PreferenceData,
  SearchType,
  Template, TemplateCode,
  UserInfo
} from '@store/user/user.model';
import { RootRoutes } from '@root/root-routes';

import { NavigationExtras, Router } from '@angular/router';
import { Action, select, Store } from '@ngrx/store';
import { SpinnerOverlayService } from '@shared/services/spinner-overlay.service';
import {
  ApplyMapLayersPreferences,
  GetRegionCoordinates,
  LoadMapPointOfInterestConfig,
  SearchSuccess, SetSelectedMySearch,
} from '@store/search-board/search-board.actions';
import { ActionName } from '@shared/models/effects-names.model';
import { ConfirmationService } from '@shared/services/confirmation.service';
import { AppState } from '@root/app-state.module';
import { DEFAULT_MSG_SHOW_DELAY, SessionTimerService } from '@shared/services/session-timer.service';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { ReportsRoutes } from '@root/reports/components/reports-routes';
import { EulaService } from '@shared/services/eula.service';
import { GetSavedProperties } from '@store/saved-properties/saved-properties.actions';
import { LoadReportUsageSummary } from '@store/reports/reports.actions';
import { DirectLinkReportData } from '@store/reports/reports.model';
import { BrowserStorageService } from '@shared/services/browser-storage.service';
import { BrowserStorageKeys } from '@shared/models/browser-storage.model';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { PreferenceUtils } from '@shared/utils/preference-utils';
import { DashboardWidget } from '@root/dashboard/models/dashboard';
import { DashboardService } from '@root/dashboard/services/dashboard-service';
import { PropertyIntelligenceService } from '@root/property-intelligence/services/property-intelligence.service';
import { PropertyIntelligenceChartSelections } from '@root/property-intelligence/models/property-intelligence-chart-detail.model';
import { PropertyIntelligencePreference } from '@root/property-intelligence/models/property-intelligence.model';
@Injectable()
export class UserEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<AppState>,
    private readonly userService: UserService,
    private readonly spinnerOverlayService: SpinnerOverlayService,
    private readonly router: Router,
    private readonly confirmationService: ConfirmationService,
    private readonly sessionTimerService: SessionTimerService,
    private readonly eulaService: EulaService,
    private readonly browserStorageService: BrowserStorageService,
    private readonly mixpanelService: MixpanelService,
    private readonly dashboardService: DashboardService,
    private readonly propertyIntelligenceService: PropertyIntelligenceService,
  ) {
  }

  login$: Observable<UserActions.LoginSuccess | UserActions.LoginNavigation | UserActions.LoginFailure> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.LOGIN),
      this.spinnerOverlayService.wrapRequest((action: UserActions.Login) => {
        return this.userService.login(action.payload).pipe(
          catchError((loginResponse: LoginResponse) => of(loginResponse))
        );
      }, ActionName.LOGIN),
      this.mapLoginStatusCode()
    ));

  saveRSSFeed$: Observable<UserActions.SaveRSSFeedSuccess> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.SAVE_RSS_FEED),
      this.spinnerOverlayService.wrapRequest((action: UserActions.SaveRSSFeed) => {
        return this.dashboardService.saveRSSFeed(action.payload).pipe(
          catchError(() => void(0)),
          map(() => new UserActions.SaveRSSFeedSuccess(action.payload))
        )
      }, ActionName.SAVE_RSS_FEED),
    ),
  );

  hideUnhideDashboardWidget$: Observable<UserActions.UpdateDashboardWidgets> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.HIDE_UNHIDE_DASHBOARD_WIDGET),
      withLatestFrom(
        this.store$.pipe(select(selectDashboardWidgets)),
      ),
      switchMap(
        (
          [action, dashboardWidgets]: Array<UserActions.HideUnhideDashboardWidget | Array<DashboardWidget>>
        ) => {
          const widgets = this.dashboardService.updateWidgetListForHide(
            (action as UserActions.HideUnhideDashboardWidget).payload,
            [...(dashboardWidgets as Array<DashboardWidget>)]
          );
          return of(new UserActions.UpdateDashboardWidgets(widgets));
        })
    )
  )

  updatePropIntelFilters$: Observable<UserActions.UpdatePropIntelPreference> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.UPDATE_PROP_INTEL_FILTERS),
      withLatestFrom(
        this.store$.pipe(select(selectPropIntelChartSelections))
      ),
      map(([action, propIntelChartSelections]: [UserActions.UpdatePropIntelFilters, PropertyIntelligenceChartSelections]) => {
        return new UserActions.UpdatePropIntelPreference({
          propertyIntelligenceChartSelection: propIntelChartSelections,
          propertyIntelligenceFilters: action.payload
        })
      })
    ));

  updatePropIntelChartSelections$: Observable<UserActions.UpdatePropIntelPreference> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.UPDATE_PROP_INTEL_CHART_SELECTIONS),
      withLatestFrom(
        this.store$.pipe(select(selectPropIntelPreference))
      ),
      map(([action, propIntelPreference]: [UserActions.UpdatePropIntelChartSelections, PropertyIntelligencePreference]) => {
        return new UserActions.UpdatePropIntelPreference({
          propertyIntelligenceFilters: action.payload.shouldNotFetchData ?
            propIntelPreference.propertyIntelligenceFilters :
            [...propIntelPreference.propertyIntelligenceFilters],
          propertyIntelligenceChartSelection: {
            ...propIntelPreference.propertyIntelligenceChartSelection,
            [action.payload.prefKey]: {
              key: action.payload.key,
              type: action.payload.type,
            },
          },
        });
      }),
    ));

  updatePropIntelPreferences$: Observable<UserActions.UpdatePropIntelPreferenceSuccess> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.UPDATE_PROP_INTEL_PREFERENCE),
      this.spinnerOverlayService.wrapRequest((action: UserActions.UpdatePropIntelPreference) => {
        return this.propertyIntelligenceService.updatePropIntelPreference(action.payload).pipe(
          catchError(() => void(0)),
          map(() => new UserActions.UpdatePropIntelPreferenceSuccess(action.payload)),
        )
      }, ActionName.UPDATE_PROP_INTEL_PREFERENCE),
    ));

  updateDashboardWidgets$: Observable<void> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.UPDATE_DASHBOARD_WIDGETS),
      this.spinnerOverlayService.wrapRequest((action: UserActions.UpdateDashboardWidgets) => {
        return this.dashboardService.updateWidgets(action.payload)
      }, ActionName.UPDATE_DASHBOARD_WIDGETS)
    ), { dispatch: false })

  refreshPage$: Observable<UserActions.LoginSuccess | UserActions.LoginNavigation | UserActions.LoginFailure> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.REFRESH_PAGE),
      this.spinnerOverlayService.wrapRequest(() => {
        return this.userService.directLogin().pipe(
          catchError((loginResponse: LoginResponse) => of(loginResponse)),
        );
      }, ActionName.REFRESH_PAGE),
      this.mapLoginStatusCode()
    ));

  /**
   * Listens for `CALLBACK_REFRESH`, calls `directLogin`, and maps the result to
   * `LoginSuccess` or `LoginFailure` without triggering navigation (D2A callback path).
   */
  callbackRefresh$: Observable<UserActions.LoginSuccess | UserActions.LoginFailure> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.CALLBACK_REFRESH),
      switchMap(() => this.userService.directLogin().pipe(
        catchError((loginResponse: LoginResponse) => of(loginResponse)),
        this.mapLoginStatusCode(void 0, false)
      ))
    ));

  loginSuccess$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.LOGIN_SUCCESS),
      tap(() => this.browserStorageService.set(BrowserStorageKeys.LOGGED_IN, 'true')),
      withLatestFrom(
        this.store$.select(selectUserInfo)
      ),
      switchMap(([action, userInfo]: [UserActions.LoginSuccess, UserInfo]) => {
        this.initializePendo(action.payload.userAccessOutput.userInfo, action.payload.mixpanelResId);
        this.mixpanelService.setup(userInfo);
        this.mixpanelService.track('Login', {
          loginSuccessful: true
        });
        const preferenceGroupList = action.payload
          && action.payload.prefAndTempData
          && action.payload.prefAndTempData.preferenceGroupList;

        if (action.payload.standAlonePropIntelEnabled) {
          return [
            new UserActions.RestartSessionTimer(),
          ]
        }

        return [
          new GetLoginData(),
          new UserActions.RestartSessionTimer(),
          new LoadMapPointOfInterestConfig(),
          new GetRegionCoordinates(preferenceGroupList),
          new ApplyMapLayersPreferences(preferenceGroupList, action.payload.premiumSearchMinimumZoom),
          new GetSavedProperties(),
          new LoadReportUsageSummary(),
        ];
      })
    ));

  loginFailure$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.LOGIN_FAILURE),
      tap((response: UserActions.LoginFailure) => {
        this.browserStorageService.remove(BrowserStorageKeys.LOGGED_IN);
        this.router.navigate([RootRoutes.Login]);

        // Super Properties are not set when login fails. Adding appId here instead.
        this.mixpanelService.track('Login', {
          appId: 'Realist',
          loginSuccessful: false,
          failureReason: response.payload.statusMessage
        });
      })
    ), {dispatch: false});

  logout$: Observable<UserActions.LogoutSuccess> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.LOGOUT),
      switchMap(() => {
          return from(this.router.navigate([RootRoutes.Logout])).pipe(
            filter(Boolean),
            tap(() => {
              this.browserStorageService.remove(BrowserStorageKeys.LOGGED_IN);
            }),
            this.spinnerOverlayService.wrapRequest(() => {
              return this.userService.logout()
                .pipe(
                  catchError(() => EMPTY)
                );
            }, ActionName.LOGOUT),
            map(() => new UserActions.LogoutSuccess()));
        }
      )
    ));

  eulaModalResponse$: Observable<UserActions.UpdateEulaStatus | UserActions.Logout> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.EULA_MODAL_RESPONSE),
      map((action: UserActions.EulaModalResponse) =>
        action.payload ? new UserActions.UpdateEulaStatus() : new UserActions.Logout()
      )
    ));

  getReplyToEmailAddress$: Observable<UserActions.GetReplyToEmailAddressSuccess> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.GET_REPLY_TO_EMAIL_ADDRESS),
      switchMap(() => {
        return this.userService.getReplyToEmailAddress().pipe(
          catchError(() => of(void (0))),
        )
      }),
      map((response: string) => new UserActions.GetReplyToEmailAddressSuccess(response)),
    )
  )

  getLoginData$: Observable<UserActions.GetLoginDataSuccess> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.GET_LOGIN_DATA),
      switchMap(() => {
        return this.userService.getLoginData().pipe(
          catchError(() => of(void (0))),
          filter(Boolean)
        );
      }),
      map((response: LoginResponse) => new UserActions.GetLoginDataSuccess(response))
    ));

  getGeoData$: Observable<UserActions.GetGeoDataSuccess> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.GET_GEO_DATA),
      withLatestFrom(this.store$.pipe(select(selectGeoData))),
      filter(([_, geoData]: [UserActions.GetGeoData, GeoData]) => {
        return !geoData;
      } ),
      switchMap(() => this.propertyIntelligenceService.getGeoData().pipe(
        catchError(() => of(void (0))),
        filter(Boolean),
      )),
      map((geoData: GeoData) => new UserActions.GetGeoDataSuccess(geoData))
    ));

  getMySearchData$: Observable<UserActions.GetMySearchDataSuccess> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.GET_MY_SEARCH_DATA),
      switchMap(() => {
        return this.userService.getMySearchData().pipe(
            catchError(() => of(void (0))),
            filter(Boolean)
          );
      }),
      map((response: LoginResponse) => {
        return new UserActions.GetMySearchDataSuccess(response);
      })
    ));

  getMySearchDataSuccess$: Observable<SetSelectedMySearch> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.GET_MY_SEARCH_DATA_SUCCESS),
      map((action: UserActions.LoginSuccess) => {
        const lastLoadedTemplate = this.getMySearchLastLoadedTemplate(action.payload.prefAndTempData);
        const defaultMySearchTemplate = lastLoadedTemplate ?
          lastLoadedTemplate :
          action.payload.prefAndTempData.templateList.find((t: Template) => t.templateCode === TemplateCode.TMPL_MY_SEARCH_DEFAULT);

        return new SetSelectedMySearch(defaultMySearchTemplate);
      })
    ));

  updateEulaStatus: Observable<void> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.UPDATE_EULA_STATUS),
      switchMap(() => this.eulaService.updateEulaStatus(DisplayOptions.YES).pipe(
        catchError(() => EMPTY)
      ))
    ), {dispatch: false});

  confirmLogout$: Observable<UserActions.Logout> = createEffect(() => this.actions$.pipe(
    ofType(UserActions.CONFIRM_LOGOUT),
    switchMap(() => this.confirmationService.openConfirmationLogoutModal().afterClosed()),
    filter(Boolean),
    map(() => new UserActions.Logout())
  ));

  restartSessionTimer: Observable<UserActions.OpenSessionExpirationModal> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.RESTART_SESSION_TIMER),
      tap(() => this.userService.keepSession().subscribe()),
      switchMap(() => {
        return combineLatest(
          [
            this.store$.pipe(select(selectSessionTimeoutDefault)),
            this.store$.pipe(select(selectSessionTimeoutMsgShowDelay))
          ]
        ).pipe(
          take(1)
        );
      }),
      switchMap(([sessionTimeoutDefault, sessionTimeoutMsgShowDelay]: [number, number]) => {
        return this.sessionTimerService.initTimer(sessionTimeoutDefault, sessionTimeoutMsgShowDelay);
      }),
      map(() => new UserActions.OpenSessionExpirationModal())
    ));

  openSessionExpirationModal$: Observable<UserActions.Logout | UserActions.KeepSession> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.OPEN_SESSION_EXPIRATION_MODAL),
        switchMap(() => {
          return this.store$.pipe(
            select(selectSessionTimeoutMsgShowDelay),
            take(1)
          );
        }),
        switchMap((sessionTimeoutMsgShowDelay: number) => {
          const dialogRef: MatDialogRef<ConfirmationModalComponent, boolean> = this.confirmationService.openSessionTimeoutModal();
          const timerSubject = new BehaviorSubject(setTimeout(() => dialogRef.close(false), DEFAULT_MSG_SHOW_DELAY));

          return race(
            this.sessionTimerService.initMsgShowTimer(sessionTimeoutMsgShowDelay).pipe(
              tap(() => dialogRef.close(false)),
              mapTo(false)
            ),
            dialogRef.afterClosed().pipe(
              tap(() => {
                timerSubject.subscribe((timer) => clearTimeout(timer));
              }))
          );
        }),
        map((keepSession: boolean) => {
          if (keepSession) {
            this.userService.keepSession()
              // If we get an error, they are logged out already, we should just send them to the logout page.
              .pipe(catchError(() => {
                this.store$.dispatch(new Logout());
                return of(EMPTY);
              })).subscribe();
          }
          return keepSession ? new UserActions.KeepSession() : new UserActions.Logout();
        })
      );
  });

  directLinkLogin$: Observable<UserActions.LoginSuccess | UserActions.LoginNavigation | UserActions.LoginFailure> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.DIRECT_LINK_LOGIN),
      this.spinnerOverlayService.wrapRequest((action: UserActions.DirectLinkLogin) => {
        return this.userService.directLogin().pipe(
          catchError((loginResponse: LoginResponse) => of(loginResponse)),
          this.mapLoginStatusCode(action.payload.searchType)
        );
      }, ActionName.DIRECT_LINK_LOGIN),
    ));

  loginNavigation$: Observable<[UserActions.LoginNavigation, boolean, boolean]> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.LOGIN_NAVIGATION),
      withLatestFrom(
        this.store$.pipe(select(selectIsDashboardEnabled)),
        this.store$.pipe(select(selectIsStandAlonePropIntelEnabled)),
      ),
      tap(([action, isDashboardEnabled, isStandAlonePropIntelEnabled]: [UserActions.LoginNavigation, boolean, boolean]) => {
        // If the user was redirected to login from a protected route (e.g. /unlock/…),
        // send them back there after a successful login.
        const returnUrl = this.consumeReturnUrl();
        if (returnUrl) {
          this.router.navigateByUrl(returnUrl, { state: { restoredReturnUrl: true } });
          return;
        }

        const searchType: SearchType = action.payload;

        // Direct-link logins are backend redirects that land on guarded /reports/* tabs.
        // Signal `fromRedirect: true` so `ReportsGuard` allows the navigation and lets the report
        // component handle its own access overlay — mirroring the D2A callback flow
        // (see CallbackRedirectComponent.navigateToReport). Without this, guarded report tabs
        // (Neighborhood Profile, Comparables, Assessor Map, Flood/Premium Flood, Market Trends)
        // are blocked and the user falls through to the Dashboard.
        const reportNavigationExtras: NavigationExtras = {
          queryParams: { searchType },
          state: { fromRedirect: true }
        };

        switch (searchType) {
          case SearchType.FLOOD_MAP:
            this.router.navigate([RootRoutes.Reports, ReportsRoutes.FloodMap], reportNavigationExtras);
            break;
          case SearchType.PREMIUM_FLOOD_MAP:
            this.router.navigate([RootRoutes.Reports, ReportsRoutes.PremiumFloodMap], reportNavigationExtras);
            break;
          case SearchType.ASSESSOR_MAP:
            this.router.navigate([RootRoutes.Reports, ReportsRoutes.AssessorMap], reportNavigationExtras);
            break;
          case SearchType.PROPERTY_DETAIL:
            this.router.navigate([RootRoutes.Reports, ReportsRoutes.PropertyDetails], reportNavigationExtras);
            break;
          case SearchType.COMPARABLE:
            this.router.navigate([RootRoutes.Reports, ReportsRoutes.Comparables], reportNavigationExtras);
            break;
          case SearchType.NEIGHBORHOOD:
            this.router.navigate([RootRoutes.Reports, ReportsRoutes.NeighborhoodProfile], reportNavigationExtras);
            break;
          case SearchType.PROPERTY_MAP:
            this.router.navigate([RootRoutes.Search], {queryParams: {searchType}});
            break;
          case SearchType.MARKET_TRENDS:
            this.router.navigate([RootRoutes.Reports, ReportsRoutes.MarketTrends], reportNavigationExtras);
            break;
          case SearchType.MAIN_APPLICATION:
          default:
            if (isStandAlonePropIntelEnabled) {
              this.router.navigate([RootRoutes.PropertyIntelligence]);
              break;
            }
            if (isDashboardEnabled) {
              this.router.navigate([RootRoutes.Dashboard]);
              break;
            }
            this.router.navigate([RootRoutes.Search]);
            break;
        }
      })
    ), {dispatch: false});

  getDirectLinkData$: Observable<UserActions.GetDirectLinkDataSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.GET_DIRECT_LINK_DATA),
      this.spinnerOverlayService.wrapRequest(() => {
        return this.userService.getDirectLinkData().pipe(
          catchError(() => of(void (0))),
          map((directLinkData: DirectLinkReportData = {} as DirectLinkReportData) => {
            return new UserActions.GetDirectLinkDataSuccess(directLinkData);
          })
        );
      }, ActionName.DIRECT_LINK)
    ));

  getDirectLinkDataSuccess$: Observable<SearchSuccess> = createEffect(() => this.actions$
    .pipe(
      ofType(UserActions.GET_DIRECT_LINK_DATA_SUCCESS),
      map((action: UserActions.GetDirectLinkDataSuccess) => {
        const searchResults = action.payload.propertySearchOutput
          && action.payload.propertySearchOutput.searchResultData;

        return new SearchSuccess(searchResults);
      })
    ));

  getHelpPageContactInfo$: Observable<UserActions.GetHelpPageContactInfoSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.GET_HELP_PAGE_CONTACT_INFO),
      this.spinnerOverlayService.wrapRequest(() => {
        return this.userService.getHelpPageContactInfo().pipe(
          catchError(() => of(void (0))),
          map((contactInfo: Array<ContactInfo>) => new UserActions.GetHelpPageContactInfoSuccess(contactInfo))
        );
      }, ActionName.GET_HELP_PAGE_CONTACT_INFO)
    ));

  /**
   * Maps a `LoginResponse` stream to typed NgRx actions.
   *
   * @overload No navigation override — emits `LoginSuccess | LoginNavigation | LoginFailure`
   * @overload `shouldNavigate: false` — emits `LoginSuccess | LoginFailure` (D2A callback use-case)
   */
  private mapLoginStatusCode(
    searchType?: SearchType,
  ): OperatorFunction<LoginResponse, UserActions.LoginSuccess | UserActions.LoginNavigation | UserActions.LoginFailure>;
  private mapLoginStatusCode(
    searchType: SearchType | undefined,
    shouldNavigate: false,
  ): OperatorFunction<LoginResponse, UserActions.LoginSuccess | UserActions.LoginFailure>;
  private mapLoginStatusCode(
    searchType?: SearchType,
    shouldNavigate = true,
  ): OperatorFunction<LoginResponse, UserActions.LoginSuccess | UserActions.LoginNavigation | UserActions.LoginFailure> {
    return mergeMap((loginResponse: LoginResponse) => {
      return (loginResponse && loginResponse.statusCode === LoginStatus.AUTHORIZED)
        ? shouldNavigate
          ? [new UserActions.LoginSuccess(loginResponse), new UserActions.LoginNavigation(searchType)]
          : [new UserActions.LoginSuccess(loginResponse)]
        : [new UserActions.LoginFailure({
          mixpanelProjectToken: loginResponse.mixpanelProjectToken,
          mixpanelResId: loginResponse.mixpanelResId,
          statusCode: loginResponse.statusCode,
          statusMessage: loginResponse.statusMessage
        })];
    });
  }

  private getMySearchLastLoadedTemplate(prefAndTempData: PreferenceData): Template | null {
    const preferenceGroup = PreferenceUtils.getPreferenceGroup(prefAndTempData.preferenceGroupList, PreferenceCode.PG_MY_SEARCH_CONFIG);
    const preferenceElement = PreferenceUtils.getPreferenceElement(preferenceGroup, PreferenceCode.PREF_MY_SEARCH_LAST_LOADED_TEMPLATE);
    const value = PreferenceUtils.getPreferenceValue(preferenceElement);
    if (!value) {
      return null;
    }

    return prefAndTempData.mySearchTemplates.find((template: Template) => template.templateCode === value);
  }

  private consumeReturnUrl(): string | null {
    const url = this.browserStorageService.get(BrowserStorageKeys.RETURN_URL);

    if (!url) {
      return null;
    }

    this.browserStorageService.remove(BrowserStorageKeys.RETURN_URL);

    if (this.isValidReturnUrl(url)) {
      return url;
    }

    return null;
  }

  private isValidReturnUrl(url: string): boolean {
    return /^\/(unlock|dashboard|search|saved-properties|orders|settings|reports)(\/.*)?$/.test(url)
      || /^\/redirect\/[^/?#]+\/[^/?#]+\/[^/?#]+([?#].*)?$/.test(url);
  }

  private initializePendo(userInfo: UserInfo, mixpanelResId: string): void {
    const visitor = {
      id: userInfo.userId,
      full_name: `${userInfo.firstName} ${userInfo.lastName}`,
      first_name: userInfo.firstName,
      last_name: userInfo.lastName,
      ResAgentid: `${mixpanelResId}-${userInfo.userId}`,
    };
    console.log('Pendo visitor data:', visitor);
    (window as any).pendo.initialize({
      visitor,
      account: {
        id: userInfo.groupCode,
      }
    });
  }
}
